import type { Project, ProjectCategory } from '@/types/portfolio';

/**
 * Format category names for display
 */
export function formatCategoryName(category: ProjectCategory): string {
  return category.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join('/');
}

/**
 * Format project status for display
 */
export function formatStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Format dates for display
 */
export function formatDate(dateString: string): string {
  return dateString;
}

/**
 * Format text with basic HTML support
 */
export function formatText(text: string): string {
  // If the text already contains block-level HTML tags, assume authoring handled layout
  if (/<(ul|ol|li|p|h[1-6]|div|section|article)/i.test(text)) {
    return text;
  }
  return text.replace(/\n/g, '<br>');
}

/**
 * Get URL parameters
 */
export function getUrlParams(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}

/**
 * Navigate to a URL
 */
export function navigateTo(url: string): void {
  window.location.href = url;
}

/**
 * Smooth scroll to top
 */
export function scrollToTop(): void {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

/**
 * Filter projects by category
 */
export function filterProjectsByCategory(
  projects: Project[], 
  category: ProjectCategory | 'all'
): Project[] {
  if (category === 'all') {
    return projects;
  }
  return projects.filter(project => project.categories.includes(category));
}

/**
 * Find project by ID
 */
export function findProjectById(projects: Project[], id: string): Project | undefined {
  return projects.find(project => project.id === id);
}

/**
 * Get next project in array
 */
export function getNextProject(projects: Project[], currentId: string): Project | null {
  const currentIndex = projects.findIndex(project => project.id === currentId);
  if (currentIndex === -1) return null;
  
  const nextIndex = (currentIndex + 1) % projects.length;
  return projects[nextIndex];
}

/**
 * Debounce function for performance
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Check if device is mobile
 */
export function isMobile(): boolean {
  return window.innerWidth <= 768;
}

/**
 * Add event listener with cleanup
 */
export function addEventListenerWithCleanup(
  element: Element,
  event: string,
  handler: EventListener,
  options?: AddEventListenerOptions
): () => void {
  element.addEventListener(event, handler, options);
  return () => element.removeEventListener(event, handler, options);
}